import React, { useState } from "react";
import { FileText, CheckCircle2, XCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccBtnPink, ccBtnGhost, EmptyState } from "../care/ui";
import { PROGRAMS } from "./opsData";

// Parent applications → daycare approval → auto-generated contract docs
export default function Applications({ ctx, role }) {
  const isParent = role === "parent";
  const [form, setForm] = useState({ childId: "", program: PROGRAMS[0], notes: "" });

  const submit = (e) => {
    e.preventDefault();
    const child = ctx.childList.find((c) => c.id === form.childId) || ctx.childList[0];
    if (!child) {
      toast.error("No child linked to this account.");
      return;
    }
    ctx.setApplications((prev) => [
      {
        id: `app_${Date.now()}`,
        parentName: ctx.currentUserName || "Parent",
        childName: `${child.firstName} ${child.lastName}`,
        program: form.program,
        facility: ctx.facilityName,
        notes: form.notes,
        status: "pending",
        submittedAt: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      },
      ...prev,
    ]);
    ctx.addLog("CREATE", "applications", `Application submitted: ${child.firstName} ${child.lastName} → ${form.program}`);
    toast.success("Application submitted to the facility.");
    setForm({ childId: "", program: PROGRAMS[0], notes: "" });
  };

  const decide = (app, approved) => {
    ctx.setApplications((prev) => prev.map((a) => (a.id === app.id ? { ...a, status: approved ? "approved" : "declined" } : a)));
    if (approved) {
      // Auto-generate binding service agreement + permission slip populated with dynamic data
      ctx.setDocs((prev) => [
        {
          id: `doc_${Date.now()}_agr`,
          childId: ctx.childList[0]?.id || "ch_1",
          childName: app.childName,
          title: `Service Agreement — ${app.childName}`,
          body: `This Service Agreement is entered into between ${ctx.facilityName} ("Facility") and ${app.parentName} ("Parent/Guardian") for enrollment of ${app.childName} in the ${app.program} program. The Facility agrees to provide childcare services in accordance with state licensing ratios and safety standards. The Parent agrees to the published tuition schedule and policies in the parent handbook. This agreement is binding upon e-signature.`,
          isSigned: false,
          signedBy: null,
          signedAt: null,
          auto: true,
        },
        {
          id: `doc_${Date.now()}_prm`,
          childId: ctx.childList[0]?.id || "ch_1",
          childName: app.childName,
          title: `Permission Slip — Activities & Field Trips (${app.childName})`,
          body: `I, ${app.parentName}, authorize ${app.childName} to participate in supervised off-site activities and field trips organized by ${ctx.facilityName}, including transportation by licensed staff vehicles. Emergency contacts on file will be notified per facility protocol.`,
          isSigned: false,
          signedBy: null,
          signedAt: null,
          auto: true,
        },
        ...prev,
      ]);
      ctx.addLog("UPDATE", "applications", `Approved ${app.childName}; service agreement + permission slip auto-generated`);
      ctx.onAlert?.({ type: "success", message: `Application approved — contract documents dispatched to ${app.parentName}.` });
      toast.success("Approved — service agreement & permission slip generated for e-signature.");
    } else {
      ctx.addLog("UPDATE", "applications", `Declined application: ${app.childName}`);
      toast("Application declined.");
    }
  };

  const statusPill = {
    pending: "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]",
    approved: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]",
    declined: "bg-rose-50 border-rose-200 text-rose-500",
  };

  return (
    <div className="space-y-5" data-testid="applications-module">
      {isParent && (
        <form onSubmit={submit} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl space-y-3 dark:bg-[#273244] dark:border-[#374457]" data-testid="application-form">
          <p className="text-xs font-extrabold font-heading text-[#374151] dark:text-slate-100">Apply to {ctx.facilityName}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={ccLabel}>Child</label>
              <select value={form.childId} onChange={(e) => setForm({ ...form, childId: e.target.value })} className={ccInput} data-testid="application-child-select">
                <option value="">Select child</option>
                {ctx.childList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={ccLabel}>Program</label>
              <select value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} className={ccInput} data-testid="application-program-select">
                {PROGRAMS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything we should know..." className={ccInput} data-testid="application-notes" />
          <button type="submit" className={ccBtnPink} data-testid="application-submit">
            <Send className="w-4 h-4 inline mr-1 -mt-0.5" /> Submit Application
          </button>
        </form>
      )}

      {ctx.applications.length === 0 ? (
        <EmptyState text="No applications yet." testId="applications-empty" />
      ) : (
        <div className="space-y-3">
          {ctx.applications.map((a) => (
            <div key={a.id} className="p-4 bg-white border border-[#FCE6E9] rounded-2xl dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`application-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">
                    {a.childName} <span className="font-semibold text-[#9CA3AF] text-xs">· {a.parentName}</span>
                  </p>
                  <p className="text-xs text-[#6B7280] dark:text-slate-300">{a.program}</p>
                  {a.notes && <p className="text-[11px] text-[#9CA3AF] mt-1">“{a.notes}”</p>}
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Submitted {a.submittedAt}</p>
                </div>
                <span className={`px-3 py-1 border text-[10px] font-extrabold uppercase rounded-full ${statusPill[a.status]}`} data-testid={`application-status-${a.id}`}>
                  {a.status}
                </span>
              </div>
              {!isParent && a.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => decide(a, true)} className={ccBtnPink} data-testid={`application-approve-${a.id}`}>
                    <CheckCircle2 className="w-4 h-4 inline mr-1 -mt-0.5" /> Approve & Generate Contract
                  </button>
                  <button onClick={() => decide(a, false)} className={ccBtnGhost} data-testid={`application-decline-${a.id}`}>
                    <XCircle className="w-4 h-4 inline mr-1 -mt-0.5" /> Decline
                  </button>
                </div>
              )}
              {isParent && a.status === "approved" && (
                <p className="text-[11px] font-bold text-[#2F6031] mt-3 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Contract documents are ready — review & e-sign in the E-Documents tab.
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
